import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Send, Building2, Shield, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', company: '', team_size: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setError('Name and email are required.'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/contact/demo`, form);
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-slate-50" data-testid="contact-page">
      {/* Header */}
      <section className="py-16 md:py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-semibold tracking-[0.1em] uppercase text-teal-600 mb-3">Get in Touch</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-5xl md:text-6xl tracking-tight font-semibold text-slate-900 mb-4">
              Choose Your Path
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-2xl leading-relaxed">
              Whether you're an HR leader looking to transform your workforce strategy, or an employee seeking to understand our privacy measures.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Two-Column Layout */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: HR Demo Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="glass-card rounded-3xl p-8 lg:p-10"
              data-testid="hr-demo-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl gradient-teal flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-medium text-slate-800">For HR Leaders</h3>
                  <p className="text-sm text-slate-500">Transform Your Workforce</p>
                </div>
              </div>

              {submitted ? (
                <div className="text-center py-12" data-testid="demo-success-msg">
                  <CheckCircle2 className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                  <h4 className="font-heading text-xl font-medium text-slate-800 mb-2">Demo Request Received</h4>
                  <p className="text-sm text-slate-500">Our team will reach out within 24 hours to schedule your personalized demo.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name</label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jane Smith"
                      className="border-slate-200 focus:ring-teal-500/30 focus:border-teal-500"
                      data-testid="demo-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Work Email</label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="jane@company.com"
                      className="border-slate-200 focus:ring-teal-500/30 focus:border-teal-500"
                      data-testid="demo-email-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Company</label>
                      <Input
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        placeholder="Acme Corp"
                        className="border-slate-200"
                        data-testid="demo-company-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Team Size</label>
                      <Input
                        value={form.team_size}
                        onChange={(e) => setForm({ ...form, team_size: e.target.value })}
                        placeholder="50-200"
                        className="border-slate-200"
                        data-testid="demo-teamsize-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Tell us what's on your mind</label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="We're interested in understanding burnout trends in our sales team..."
                      rows={4}
                      className="border-slate-200 focus:ring-teal-500/30 focus:border-teal-500"
                      data-testid="demo-message-input"
                    />
                  </div>
                  {error && <p className="text-sm text-red-500" data-testid="demo-error-msg">{error}</p>}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white h-11"
                    data-testid="demo-submit-btn"
                  >
                    {loading ? 'Submitting...' : 'Schedule a Strategy Call'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>

                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 mb-2">Why Udvega?</p>
                    <ul className="space-y-2">
                      {['ISO-Standard Data Privacy', 'Real-time Team Health Monitoring', 'AI-Backed Decision Making'].map((item) => (
                        <li key={item} className="flex items-center gap-2 text-xs text-slate-500">
                          <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </form>
              )}
            </motion.div>

            {/* Right: Employee Privacy Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="glass-card rounded-3xl p-8 lg:p-10 flex flex-col"
              data-testid="employee-privacy-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-medium text-slate-800">For Employees</h3>
                  <p className="text-sm text-slate-500">Your Identity is Our Priority</p>
                </div>
              </div>

              <div className="space-y-6 flex-1">
                <div>
                  <h4 className="font-heading font-medium text-slate-800 mb-2">How is my data protected?</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    Every response you provide is encrypted end-to-end. Your name, employee ID, and personal identifiers are stripped before any data reaches our ML model. HR only sees aggregated team scores — never individual answers.
                  </p>
                </div>

                <div>
                  <h4 className="font-heading font-medium text-slate-800 mb-2">Can my manager see my answers?</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    No. Udvega enforces a strict minimum threshold of 5 respondents per team before any results are displayed. This makes it mathematically impossible to trace any response back to an individual.
                  </p>
                </div>

                <div>
                  <h4 className="font-heading font-medium text-slate-800 mb-2">What parameters are measured?</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    Our survey measures three scientifically validated constructs: Workplace Culture Friction (7 items), Burnout Risk Score (9 items), and Team Adaptability Index (20 items). All scales are derived from peer-reviewed psychological research.
                  </p>
                </div>

                <div>
                  <h4 className="font-heading font-medium text-slate-800 mb-2">What happens after I take the survey?</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    Your encrypted response is processed by our ML model alongside other anonymous responses. The model identifies team-level patterns and risks, enabling your organization to improve workplace conditions proactively.
                  </p>
                </div>
              </div>

              <div className="mt-8 p-4 rounded-2xl bg-slate-900 text-center">
                <p className="text-sm text-slate-300 mb-2">Want to read our full privacy whitepaper?</p>
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="privacy-whitepaper-btn">
                  Download Privacy Whitepaper
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
